<template>
  <div>
    <div
      v-show="isVisible"
      class="ai-spotlight-panel"
      :class="{ 'is-mobile': isMobile }"
      role="complementary"
      aria-label="MSDAT AI Spotlight insights panel"
    >
      <div class="panel-header d-flex justify-content-between align-items-center p-3 border-bottom">
        <div class="d-flex align-items-center flex-wrap">
          <b-icon-stars class="text-success mr-2" aria-hidden="true" />
          <h5 id="ai-spotlight-title" class="m-0 font-weight-bold">AI Spotlight</h5>
          <b-badge variant="warning" class="ml-2">Beta</b-badge>
          <b-icon-info-circle
            class="ml-2 text-muted"
            font-scale="1"
            tabindex="0"
            role="img"
            aria-label="How AI Spotlight works"
            v-b-tooltip.hover.html="helpTooltipHtml"
          />
        </div>
        <b-button
          size="sm"
          variant="link"
          class="text-dark p-0"
          aria-label="Close AI Spotlight panel"
          @click="closePanel"
        >
          <b-icon-x font-scale="1.5" />
        </b-button>
      </div>

      <div class="panel-body p-3 overflow-auto" style="height: calc(100% - 118px);">
        <div v-if="computationError" class="text-center py-4 px-2">
          <b-icon-exclamation-octagon class="text-warning mb-2" font-scale="2" />
          <p class="small font-weight-bold mb-1">Spotlight temporarily unavailable</p>
          <p class="text-muted small mb-3">Something went wrong while reading the charts. You can try again.</p>
          <b-button size="sm" variant="success" @click="retryCompute">Retry</b-button>
        </div>

        <template v-else>
          <div v-if="isLoading" class="spotlight-skeleton" aria-busy="true" aria-live="polite">
            <p class="small text-muted mb-3">Analyzing the current dashboard view…</p>
            <div v-for="n in 3" :key="n" class="mb-3">
              <b-skeleton height="72px" class="rounded" />
            </div>
          </div>

          <template v-else-if="hasAnyContent">
            <p class="small text-muted mb-3">
              Insights are computed on the fly from the charts and filters currently on screen (no chat, no server storage).
            </p>

            <AiInsightCard
              v-if="isAiLoading || aiInsightText"
              :is-loading="isAiLoading"
              :insight-text="aiInsightText"
            />

            <AnomalyCard
              v-if="triangulation && triangulation.kind === 'anomaly'"
              :anomaly="triangulation"
              :explanation="anomalyExplanation"
              :why-matters="anomalyWhyMatters"
              @highlight-anomaly="handleHighlight"
            />

            <ConsistentSourcesCard
              v-if="triangulation && triangulation.kind === 'consistent'"
              :triangulation="triangulation"
              :explanation="consistentExplanation"
              :why-matters="consistentWhyMatters"
            />
            <NoDataCompareCard
              v-if="!triangulation || triangulation.kind === 'insufficient'"
              title="Triangulation anomaly"
            />

            <ClusterCard
              v-if="insightData.cluster"
              :clusters-data="insightData.cluster"
              @filter-cluster="handleClusterFilter"
            />
            <NoDataCompareCard
              v-else
              title="Peer clusters"
            />

            <TrendCard
              v-if="insightData.trend"
              :trend="insightData.trend"
              :explanation="trendExplanation"
              :why-matters="trendWhyMatters"
              @overlay-trend="handleTrendOverlay"
            />
            <NoDataCompareCard
              v-else
              title="Trend forecast"
            />

          </template>

          <div v-else class="text-center py-5">
            <b-icon-inbox class="text-muted mb-3" font-scale="2" />
            <p class="text-muted small mb-0">
              No insights available for the current selection. Try another indicator, geography, or year; or open a chart with multiple sources or regions.
            </p>
          </div>
        </template>
      </div>
      <div class="panel-footer p-2 border-top bg-white">
        <b-button block size="sm" variant="outline-secondary" @click="resetChartHighlights">
          Reset chart highlights
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import Highcharts from 'highcharts';
import AnomalyCard from './Cards/AnomalyCard.vue';
import ClusterCard from './Cards/ClusterCard.vue';
import TrendCard from './Cards/TrendCard.vue';
import ConsistentSourcesCard from './Cards/ConsistentSourcesCard.vue';
import NoDataCompareCard from './Cards/NoDataCompareCard.vue';
import AiInsightCard from './Cards/AiInsightCard.vue';
import aiSpotlightEngine from '../../modules/msdat-dashboard/mixins/ai-spotlight-engine';
import spotlightAiService from '../../services/spotlightAiService';

const LS_KEY_VISIBLE = 'msdat_ai_spotlight_panel_open';
const FORECAST_SERIES_NAME = 'MSDAT AI Spotlight (forecast)';
const SOURCE_HINTS = ['routine', 'survey', 'global', 'ndhs', 'mics', 'dhis', 'nmis'];
const GENERIC_SERIES_NAMES = ['nigeria', 'series', 'value', 'all'];

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export default {
  name: 'AISpotlight',
  components: {
    AnomalyCard,
    ClusterCard,
    TrendCard,
    ConsistentSourcesCard,
    NoDataCompareCard,
    AiInsightCard,
  },
  mixins: [aiSpotlightEngine],
  data() {
    return {
      isVisible: false,
      isLoading: false,
      isMobile: typeof window !== 'undefined' && window.innerWidth < 768,
      insightData: {
        cluster: null,
        trend: null,
      },
      triangulation: null,
      computationError: false,
      debounceTimer: null,
      isAiLoading: false,
      aiInsightText: '',
    };
  },
  computed: {
    helpTooltipHtml() {
      return `<div class="text-left" style="max-width:280px">
        <strong>How it works</strong><br/>
        Spotlight reads your <em>visible</em> Highcharts on this page and highlights triangulation gaps,
        simple peer clusters, and a one-year linear projection from recent years.
        Click a card to adjust chart emphasis — use <strong>Reset chart highlights</strong> to undo.
      </div>`;
    },
    hasAnyContent() {
      return true;
    },
    anomalyExplanation() {
      const t = this.triangulation;
      if (!t || t.kind !== 'anomaly') return '';
      const a = this.stripHtml((t.maxSource && (t.maxSource.rawName || t.maxSource.name)) || '');
      const b = this.stripHtml((t.minSource && (t.minSource.rawName || t.minSource.name)) || '');
      return `Sources disagree by about ${t.diffPercent}% in this view — compare ${a} and ${b}.`;
    },
    anomalyWhyMatters() {
      return 'Large gaps between routine, survey, and global estimates can signal data quality issues or different definitions — worth investigating before decisions.';
    },
    consistentExplanation() {
      const t = this.triangulation;
      if (!t || t.kind !== 'consistent') return '';
      return `Selected sources line up within the ${t.threshold}% triangulation threshold for this chart.`;
    },
    consistentWhyMatters() {
      return 'When sources agree, triangulation is more trustworthy for planning and reporting.';
    },
    trendExplanation() {
      const t = this.insightData.trend;
      if (!t) return '';
      return `Simple linear trend on the last ${t.yearsUsed} year(s) shown in the chart — not a full forecast model.`;
    },
    trendWhyMatters() {
      return 'Directional signals help prioritise follow-up; always cross-check with programme context.';
    },
  },
  created() {
    if (typeof window !== 'undefined' && localStorage.getItem(LS_KEY_VISIBLE) === 'true') {
      this.isVisible = true;
    }
    this.$root.$on('toggle-ai-spotlight', this.togglePanel);
    window.addEventListener('resize', this.onResize);
  },
  mounted() {
    if (this.isVisible) {
      this.$nextTick(() => this.computeInsights());
    }
  },
  destroyed() {
    this.$root.$off('toggle-ai-spotlight', this.togglePanel);
    window.removeEventListener('resize', this.onResize);
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
  },
  watch: {
    '$store.state.MSDAT_STORE.controlConfig': {
      deep: true,
      handler() {
        this.scheduleRecompute();
      },
    },
    '$store.state.MSDAT_STORE.selectedConfigurations': {
      deep: true,
      handler() {
        this.scheduleRecompute();
      },
    },
  },
  methods: {
    onResize() {
      this.isMobile = window.innerWidth < 768;
    },
    persistVisibility() {
      if (typeof window === 'undefined') return;
      localStorage.setItem(LS_KEY_VISIBLE, this.isVisible ? 'true' : 'false');
    },
    togglePanel() {
      this.isVisible = !this.isVisible;
      this.persistVisibility();
      // eslint-disable-next-line no-console
      console.log('[AI Spotlight] panel_toggle', { open: this.isVisible });
      if (this.isVisible) {
        this.computeInsights();
      }
    },
    closePanel() {
      this.isVisible = false;
      this.persistVisibility();
    },
    scheduleRecompute() {
      if (!this.isVisible) return;
      if (this.debounceTimer) clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => this.computeInsights(), 450);
    },
    retryCompute() {
      this.computationError = false;
      this.computeInsights();
    },
    stripHtml(str) {
      if (!str) return '';
      return String(str).replace(/<[^>]*>/g, '').trim();
    },
    getSubtitleText(chart) {
      const fromUser = chart && chart.userOptions && chart.userOptions.subtitle && chart.userOptions.subtitle.text;
      const fromOpts = chart && chart.options && chart.options.subtitle && chart.options.subtitle.text;
      const txt = this.stripHtml(fromUser || fromOpts || '');
      return txt;
    },
    sourceNameFromChart(chart, fallback, chartIndex, seriesIndex) {
      const cleanFallback = this.stripHtml(fallback || '');
      const subtitleText = this.getSubtitleText(chart).toLowerCase();
      const subtitleSource = SOURCE_HINTS.find((h) => subtitleText.includes(h));
      const looksGeneric = GENERIC_SERIES_NAMES.includes(cleanFallback.toLowerCase());

      if (subtitleSource && looksGeneric) return subtitleSource;
      if (!cleanFallback) return `source-${chartIndex + 1}-${seriesIndex + 1}`;
      if (looksGeneric && !subtitleSource) return `${cleanFallback} (${chartIndex + 1}-${seriesIndex + 1})`;
      return cleanFallback;
    },
    seriesAverage(series) {
      const points = series.points || [];
      const values = points
        .map((p) => (p && p.y !== undefined && p.y !== null ? Number(p.y) : null))
        .filter((v) => v !== null && !Number.isNaN(v));
      const arr = values.length ? values : (series.yData || []).map((v) => Number(v)).filter((v) => !Number.isNaN(v));
      if (!arr.length) return null;
      return arr.reduce((sum, n) => sum + n, 0) / arr.length;
    },
    seriesNameMatches(seriesName, targetName) {
      const a = this.stripHtml(seriesName).toLowerCase();
      const b = this.stripHtml(targetName).toLowerCase();
      if (!a || !b) return false;
      if (a === b) return true;
      if (a.includes(b) || b.includes(a)) return true;
      const tokens = b.split(/[\s|,/&]+/).filter((t) => t.length > 2);
      return tokens.some((t) => a.includes(t));
    },
    getCharts() {
      if (!Array.isArray(Highcharts.charts)) {
        return [];
      }
      return Highcharts.charts.filter((c) => c && c.series && c.series.length > 0);
    },
    categoriesLookLikeYears(categories) {
      if (!categories || !categories.length) return false;
      return categories.every((c) => {
        const y = parseInt(c, 10);
        return !Number.isNaN(y) && y > 1990 && y < 2100;
      });
    },
    async computeInsights() {
      this.isLoading = true;
      this.computationError = false;
      const started = typeof performance !== 'undefined' ? performance.now() : Date.now();

      try {
        await sleep(120);

        const charts = this.getCharts();
        if (charts.length === 0) {
          this.triangulation = null;
          this.insightData = { cluster: null, trend: null };
          return;
        }

        const dataSourcesData = [];
        const peerData = [];
        const trendData = [];

        charts.forEach((chart, chartIndex) => {
          if (!chart || !chart.series) return;
          chart.series.forEach((series, seriesIndex) => {
            if (series.visible === false) return;

            const nameLower = series.name ? this.stripHtml(series.name).toLowerCase() : '';
            const looksLikeSource = SOURCE_HINTS.some((s) => nameLower.includes(s));
            const points = series.points || [];
            const yValuesFromPoints = points
              .map((p) => (p && p.y !== undefined && p.y !== null ? Number(p.y) : null))
              .filter((v) => v !== null && !Number.isNaN(v));
            const yValues = yValuesFromPoints.length
              ? yValuesFromPoints
              : (series.yData || []).map((v) => Number(v)).filter((v) => !Number.isNaN(v));
            const latestY = yValues.length ? yValues[yValues.length - 1] : null;

            if (latestY !== null && looksLikeSource) {
              dataSourcesData.push({
                name: this.sourceNameFromChart(chart, series.name, chartIndex, seriesIndex),
                rawName: this.stripHtml(series.name || ''),
                value: latestY,
              });
            }

            const xAxis = chart.xAxis && chart.xAxis[0];
            const categories = xAxis && xAxis.categories;
            if (categories && series.yData && categories.length === series.yData.length) {
              const isYears = this.categoriesLookLikeYears(categories);

              if (!isYears) {
                categories.forEach((cat, idx) => {
                  if (typeof cat === 'string' && series.yData[idx] !== null && !Number.isNaN(Number(series.yData[idx]))) {
                    peerData.push({
                      location: this.stripHtml(cat),
                      value: Number(series.yData[idx]),
                    });
                  }
                });
              } else {
                categories.forEach((cat, idx) => {
                  if (series.yData[idx] !== null && !Number.isNaN(Number(series.yData[idx]))) {
                    trendData.push({
                      year: parseInt(cat, 10),
                      value: Number(series.yData[idx]),
                    });
                  }
                });
              }
            } else if (points.length > 0) {
              // Fallback for charts using [x,y] points without xAxis categories
              const candidateYears = points
                .map((p) => (p && p.x !== undefined ? parseInt(p.x, 10) : NaN))
                .filter((y) => !Number.isNaN(y) && y > 1990 && y < 2100);
              const xDataYears = (series.xData || [])
                .map((x) => parseInt(x, 10))
                .filter((y) => !Number.isNaN(y) && y > 1990 && y < 2100);
              const yearPool = candidateYears.length ? candidateYears : xDataYears;
              if (yearPool.length >= 2) {
                points.forEach((p) => {
                  const y = p && p.y !== undefined ? Number(p.y) : NaN;
                  const yr = p && p.x !== undefined ? parseInt(p.x, 10) : NaN;
                  if (!Number.isNaN(y) && !Number.isNaN(yr) && yr > 1990 && yr < 2100) {
                    trendData.push({ year: yr, value: y });
                  }
                });
                if (!trendData.length && xDataYears.length && series.yData && series.yData.length === xDataYears.length) {
                  xDataYears.forEach((yr, idx) => {
                    const y = Number(series.yData[idx]);
                    if (!Number.isNaN(y)) trendData.push({ year: yr, value: y });
                  });
                }
              } else {
                points.forEach((p) => {
                  const y = p && p.y !== undefined ? Number(p.y) : NaN;
                  const label = this.stripHtml(String(p.category != null ? p.category : p.name || ''));
                  if (!Number.isNaN(y) && label) {
                    peerData.push({ location: label, value: y });
                  }
                });
              }
            }
          });
        });

        const uniquePeersMap = new Map();
        peerData.forEach((p) => uniquePeersMap.set(p.location, p));
        const deduplicatedPeers = Array.from(uniquePeersMap.values());

        const uniqueTrendMap = new Map();
        trendData.forEach((t) => uniqueTrendMap.set(t.year, t));
        const deduplicatedTrend = Array.from(uniqueTrendMap.values());

        // Keep distinct source/value combinations. Some pages have same display name per panel.
        const seen = new Set();
        const deduplicatedSources = dataSourcesData.filter((s) => {
          const key = `${this.stripHtml(s.name)}::${Number(s.value).toFixed(3)}::${this.stripHtml(s.rawName || '')}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        }).map((s) => ({ name: this.stripHtml(s.name), rawName: this.stripHtml(s.rawName || ''), value: s.value }));

        const triangulation = this.calculateSpotlightAnomaly(deduplicatedSources, 15);
        const cluster = this.calculateSpotlightClusters(deduplicatedPeers);
        const trend = this.calculateSpotlightTrend(deduplicatedTrend);

        this.triangulation = triangulation;
        this.insightData = {
          cluster,
          trend,
        };

        const dashboardContext = this.$store.state.MSDAT_STORE.selectedConfigurations || {};
        this.fetchAiInsights({
          triangulation, cluster, trend, dashboardContext,
        });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[AI Spotlight] computation_error', e);
        this.computationError = true;
        this.triangulation = null;
        this.insightData = { cluster: null, trend: null };
      } finally {
        const elapsed = (typeof performance !== 'undefined' ? performance.now() : Date.now()) - started;
        const minSkeleton = 320;
        if (elapsed < minSkeleton) await sleep(minSkeleton - elapsed);
        if (elapsed > 2800) {
          // eslint-disable-next-line no-console
          console.warn('[AI Spotlight] slow_compute_ms', Math.round(elapsed));
        }
        this.isLoading = false;
      }
    },

    async fetchAiInsights(contextData) {
      if ((!contextData.triangulation || contextData.triangulation.kind === 'insufficient') && !contextData.cluster && !contextData.trend) {
        this.aiInsightText = '';
        return;
      }
      this.isAiLoading = true;
      this.aiInsightText = '';
      try {
        const insight = await spotlightAiService.generateSpotlightInsight(contextData);
        this.aiInsightText = insight;
      } catch (e) {
        this.aiInsightText = '**AI Deep Dive Unavailable**\n\nThe LLM service is temporarily unreachable.';
      } finally {
        this.isAiLoading = false;
      }
    },

    resetChartHighlights() {
      const charts = this.getCharts();
      charts.forEach((chart) => {
        const xAxis = chart.xAxis && chart.xAxis[0];
        const categories = xAxis && xAxis.categories ? xAxis.categories : [];
        if (xAxis && xAxis.removePlotBand) {
          categories.forEach((_, idx) => xAxis.removePlotBand(`ai-peer-${idx}`));
        }
        chart.series.forEach((s) => {
          if (this.stripHtml(s.name) === FORECAST_SERIES_NAME) {
            s.remove(false);
          }
        });
        chart.series.forEach((s) => {
          s.setVisible(true, false);
          (s.points || []).forEach((pt) => {
            try {
              if (pt.graphic && typeof pt.graphic.attr === 'function') {
                pt.graphic.attr({ opacity: 1 });
              }
            } catch (e) { /* noop */ }
          });
        });
        chart.redraw();
      });
    },

    handleHighlight(anomaly) {
      // eslint-disable-next-line no-console
      console.log('[AI Spotlight] card_click', { type: 'anomaly', anomaly });
      const maxN = anomaly.maxSource && (anomaly.maxSource.rawName || anomaly.maxSource.name);
      const minN = anomaly.minSource && (anomaly.minSource.rawName || anomaly.minSource.name);
      const charts = this.getCharts();
      let matchedAny = false;

      charts.forEach((chart) => {
        chart.series.forEach((series) => {
          const hit = this.seriesNameMatches(series.name, maxN) || this.seriesNameMatches(series.name, minN);
          if (hit) matchedAny = true;
          series.setVisible(!!hit, false);
        });
        chart.redraw();
      });

      if (!matchedAny) {
        // fallback: on the densest chart, highlight lowest-vs-highest average series
        const densest = charts
          .map((c) => ({ chart: c, visible: c.series.filter((s) => s.visible !== false) }))
          .filter((x) => x.visible.length >= 2)
          .sort((a, b) => b.visible.length - a.visible.length)[0];

        if (densest) {
          const scored = densest.visible
            .map((s) => ({ series: s, avg: this.seriesAverage(s) }))
            .filter((x) => x.avg !== null)
            .sort((a, b) => a.avg - b.avg);
          if (scored.length >= 2) {
            const keep = new Set([scored[0].series, scored[scored.length - 1].series]);
            densest.chart.series.forEach((s) => s.setVisible(keep.has(s), false));
            densest.chart.redraw();
            matchedAny = true;
          }
        }
      }

      if (!matchedAny) {
        this.$bvToast.toast('Could not map source names on this chart yet. Try another chart section.', {
          title: 'AI Spotlight',
          variant: 'warning',
          autoHideDelay: 4500,
          solid: true,
        });
      }
    },

    handleClusterFilter(cluster) {
      // eslint-disable-next-line no-console
      console.log('[AI Spotlight] card_click', { type: 'cluster', cluster });
      const names = new Set(
        (cluster.items || []).map((i) => this.stripHtml(i.location).toLowerCase()),
      );
      if (names.size === 0) return;

      const charts = this.getCharts();
      charts.forEach((chart) => {
        const xAxis = chart.xAxis && chart.xAxis[0];
        const categories = xAxis && xAxis.categories;
        if (categories && this.categoriesLookLikeYears(categories)) return;

        // remove old spotlight peer bands
        if (xAxis && xAxis.removePlotBand && categories) {
          categories.forEach((_, idx) => xAxis.removePlotBand(`ai-peer-${idx}`));
        }

        chart.series.forEach((series) => {
          if (!series.points || !series.points.length) return;
          series.points.forEach((pt) => {
            const cat = this.stripHtml(String(pt.category != null ? pt.category : pt.name || '')).toLowerCase();
            const dim = cat && !names.has(cat);
            try {
              if (pt.graphic && typeof pt.graphic.attr === 'function') {
                pt.graphic.attr({ opacity: dim ? 0.22 : 1 });
              }
              if (series.group && typeof series.group.attr === 'function') {
                series.group.attr({ opacity: 1 });
              }
              if (series.graph && typeof series.graph.attr === 'function') {
                series.graph.attr({ opacity: 1 });
              }
            } catch (e) { /* noop */ }
          });
        });

        // clear visual spotlight across selected peers using x-axis bands
        if (categories) {
          categories.forEach((c, idx) => {
            const catLower = this.stripHtml(c).toLowerCase();
            if (names.has(catLower) && xAxis && xAxis.addPlotBand) {
              xAxis.addPlotBand({
                id: `ai-peer-${idx}`,
                from: idx - 0.45,
                to: idx + 0.45,
                color: 'rgba(0, 125, 83, 0.10)',
              });
            }
          });
        }
        chart.redraw();
      });

      this.$bvToast.toast(`Emphasised ${names.size} peer areas on category charts. Use Reset to clear.`, {
        title: 'AI Spotlight',
        variant: 'info',
        autoHideDelay: 4000,
        solid: true,
      });
    },

    handleTrendOverlay(trend) {
      // eslint-disable-next-line no-console
      console.log('[AI Spotlight] card_click', { type: 'trend', trend });
      const charts = this.getCharts();
      let added = 0;

      charts.forEach((chart) => {
        const xAxis = chart.xAxis && chart.xAxis[0];
        const categories = xAxis && xAxis.categories;
        const usesCategories = !!(categories && categories.length);
        if (usesCategories && !this.categoriesLookLikeYears(categories)) return;

        chart.series
          .filter((s) => this.stripHtml(s.name) === FORECAST_SERIES_NAME)
          .forEach((s) => s.remove(false));

        const y1 = parseFloat(trend.currentValue);
        const y2 = parseFloat(trend.projectedValue);
        if (Number.isNaN(y1) || Number.isNaN(y2)) return;

        let dataPoints = [];
        if (usesCategories) {
          const years = categories.map((c) => parseInt(c, 10));
          let i1 = years.indexOf(trend.currentYear);
          if (i1 < 0) i1 = years.length - 1;
          const i2 = i1 + 1;
          if (i2 >= categories.length && xAxis && xAxis.setCategories) {
            xAxis.setCategories([...categories, String(trend.nextYear)], false);
          }
          dataPoints = [[i1, y1], [i2, y2]];
        } else {
          dataPoints = [[trend.currentYear, y1], [trend.nextYear, y2]];
        }

        chart.addSeries({
          type: 'line',
          name: FORECAST_SERIES_NAME,
          data: dataPoints,
          dashStyle: 'ShortDash',
          color: '#5b21b6',
          lineWidth: 2,
          marker: { enabled: true, radius: 4 },
          enableMouseTracking: true,
          zIndex: 8,
        }, false);
        chart.redraw();
        added += 1;
      });

      if (added === 0) {
        this.$bvToast.toast('No year-based chart found to overlay. Open a time series view.', {
          title: 'AI Spotlight',
          variant: 'warning',
          autoHideDelay: 5000,
          solid: true,
        });
      }
    },
  },
};
</script>

<style scoped>
.ai-spotlight-panel {
  position: fixed;
  top: 80px;
  right: 0;
  width: 320px;
  height: calc(100vh - 80px);
  background-color: #fafbfc;
  box-shadow: -4px 0 15px rgba(0, 0, 0, 0.1);
  z-index: 1040;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-left: 1px solid #e0e0e0;
}

.ai-spotlight-panel.is-mobile {
  top: auto;
  bottom: 0;
  width: 100%;
  height: 60vh;
  max-height: 85vh;
  box-shadow: 0 -4px 15px rgba(0, 0, 0, 0.1);
  border-left: none;
  border-top: 1px solid #e0e0e0;
  border-radius: 16px 16px 0 0;
}

.panel-body::-webkit-scrollbar {
  width: 4px;
}
.panel-body::-webkit-scrollbar-thumb {
  background-color: #ccc;
  border-radius: 4px;
}
.panel-footer {
  position: sticky;
  bottom: 0;
  z-index: 2;
}
.panel-footer .btn {
  color: #004a33 !important;
  border-color: #007d53 !important;
  font-weight: 700;
  background: #f0fff8;
}
</style>
